/**
 * HLOS-136 — useBootOrchestrator (Composable Vue 3)
 *
 * Extraído de `App.vue` (linhas 69-204) para reduzir o root component
 * de 298 → < 100 linhas. Encapsula:
 *
 *   1. **Lifecycle de boot:** `auth.init()` se locked (HLOS-45 v0.4.2: OPFS default)
 *   2. **Boot pós-unlock:** `restoreSession` + dispatch INITIALIZE state (HLOS-46)
 *   3. **Yjs provider init:** lazy via `VITE_RELAY_BASE_URL` env (HLOS-54)
 *   4. **Persist onboarding flag:** `markOnboardingComplete` (HLOS-45 v0.4.3)
 *
 * **Constitucional:**
 *   - **CL-09 (Explicabilidade):** `phase` ref rastreável + `error` ref
 *   - **CL-07 (local-first):** zero fetch externo (Yjs é opt-in via env)
 *   - **Princípio V (Aprendiz Decide):** state machine intacta — apenas orquestra
 *   - **CC-01..08:** N/A (refactor interno, sem UX)
 *   - **LGPD:** N/A (não muda dados pessoais)
 *
 * **Por que um composable (não service / store)?**
 *   - **Idempotência natural:** `run()` é no-op se já ready (sem DI complexa)
 *   - **Watch integrado:** re-roda boot em unlock subsequente (HLOS-44)
 *   - **Vue 3 idiomatic:** lifecycle hooks + reactive ref
 *
 * **Idempotência:**
 *   - `run()` é no-op se `phase === 'ready'`
 *   - `markOnboardingComplete()` é no-op se flag já set
 *   - `initializeYjsProvider()` é no-op se provider já existe
 *
 * **Ref:**
 *   - HLOS-44 (re-restart sessão em unlock subsequente)
 *   - HLOS-45 v0.4.2/v0.4.3 (OPFS default + onboarding persist)
 *   - HLOS-46 (INITIALIZE state baseado em restoreSession)
 *   - HLOS-54 (YjsProvider lazy init)
 *   - ADR-0005 (Yjs CRDT sync)
 *   - HLOS-133 (Epic pai)
 */

import { ref, watch } from "vue";
import { useAuthStore } from "../auth/store";
import { useCompanionStore } from "../companion/store";
import { useConversationStore } from "../conversation/store";
import { getVaultAPI } from "../vault/adapter";

// ─────────────────────────────────────────────────────────────────────────────
// Tipos públicos
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Fases do boot orchestrator (CL-09: rastreabilidade).
 *
 *   idle     → estado inicial, antes de `run()` ser chamado
 *   init     → `auth.init()` em progresso (cria vault OPFS, detecta lock)
 *   restoring → `restoreSession()` em progresso (lê vaultData)
 *   ready    → boot completo, usuário pode interagir
 *   error    → algo falhou (ver `error` ref para detalhes)
 */
export type BootPhase = "idle" | "init" | "restoring" | "ready" | "error";

/** Output público de `useBootOrchestrator()`. */
export interface UseBootOrchestratorReturn {
  /** Fase atual do boot (CL-09). */
  readonly phase: Readonly<typeof phase>;
  /** Erro (se phase === 'error'). */
  readonly error: Readonly<typeof error>;
  /** Idempotente: chama `auth.init()` + `bootAfterUnlock()`. */
  run: () => Promise<void>;
  /** Persiste flag `onboardingCompleted` no vault (HLOS-45 v0.4.3). */
  markOnboardingComplete: () => Promise<void>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Estado reativo (module-level para garantir singleton dentro do composable)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Fase atual — compartilhada entre consumers via `useBootOrchestrator()`.
 * Exportada para permitir tests diretos sem montar o composable Vue.
 */
const phase = ref<BootPhase>("idle");

/**
 * Erro atual — `null` enquanto não há falha.
 * Exportada para permitir tests diretos sem montar o composable Vue.
 */
const error = ref<Error | null>(null);

/** Reseta estado (usado em tests e para forçar re-boot). */
function reset(): void {
  phase.value = "idle";
  error.value = null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Composable
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Orquestrador de boot do HLOS UI Shell.
 *
 * **Ordem de inicialização (`run()`):**
 *   1. Se `auth.state === 'loading'` (vault ainda não detectado), chama
 *      `auth.init()` (HLOS-45 v0.4.2: OPFS default).
 *   2. Se unlocked, executa `bootAfterUnlock()`:
 *      a. `companion.restoreSession()`
 *      b. Yjs provider init (opt-in via `VITE_RELAY_BASE_URL`)
 *      c. Decide INITIALIZE state baseado em vault data
 *   3. Watcher em `auth.isUnlocked` re-roda boot em unlock subsequente.
 *
 * **Idempotência:** `run()` é no-op se `phase === 'ready'`. Para forçar
 * re-boot, reset manual do `phase` (não exposto — emergência apenas).
 *
 * @example
 *   // Em App.vue:
 *   const { phase, error, run, markOnboardingComplete } = useBootOrchestrator();
 *   onMounted(run);
 *   watch(() => auth.isUnlocked, (unlocked) => { if (unlocked) run(); });
 */
export function useBootOrchestrator(): UseBootOrchestratorReturn {
  const auth = useAuthStore();
  const companion = useCompanionStore();

  /**
   * Executa o boot completo. Idempotente.
   *
   * @throws nunca — erros são capturados e expostos via `error` ref
   */
  async function run(): Promise<void> {
    // Idempotência: se já ready, no-op.
    if (phase.value === "ready") return;

    error.value = null;
    phase.value = "init";

    try {
      // 1. HLOS-45 v0.4.2: OPFS default. auth.init() detecta se vault
      //    existe em OPFS e transita para 'locked' ou 'setup'.
      if (!auth.isUnlocked && auth.state === "loading") {
        await auth.init();
      }

      // 2. Se ainda locked/setup, retorna — usuário precisa unlock/criar
      //    vault no AuthState antes de continuar boot.
      if (!auth.isUnlocked) {
        phase.value = "ready"; // boot "parcial" — UI mostra AuthState
        return;
      }

      // 3. HLOS-44: restore session + decide INITIALIZE state.
      phase.value = "restoring";
      const restored = await companion.restoreSession();

      // 4. HLOS-54: YjsProvider opt-in via env.
      await tryInitializeYjsProvider();

      // 5. HLOS-46: dispatch INITIALIZE baseado em session restaurada.
      decideInitialState(restored);

      phase.value = "ready";
    } catch (e) {
      error.value = e instanceof Error ? e : new Error(String(e));
      phase.value = "error";
    }
  }

  /**
   * Persiste flag `onboardingCompleted` no vault (HLOS-45 v0.4.3).
   *
   * Necessário para que próximo boot saiba pular onboarding
   * (App.vue:213-216 originalmente).
   */
  async function markOnboardingComplete(): Promise<void> {
    try {
      await auth.markOnboardingComplete();
    } catch (e) {
      error.value = e instanceof Error ? e : new Error(String(e));
      throw e;
    }
  }

  // HLOS-44: re-restart boot em unlock subsequente (após lock manual).
  // HLOS-172 FIX v3: reset phase para forçar re-run.
  // Bug v2: o watch NÃO chamava run() quando phase.value === "ready"
  // (já tinha rodado no boot inicial), então decideInitialState nunca era
  // chamado após unlock em WKWebView reload (back de /privacy-policy.html
  // recarrega a página, deixando state="onboarding" mesmo com vault unlocked).
  // Fix: ao detectar unlock, resetar phase para "idle" e chamar run()
  // — run() faz decideInitialState() que transita para home/conversation
  // baseado em hasCompletedOnboarding() (cache populado por Fix v2).
  watch(
    () => auth.isUnlocked,
    async (unlocked) => {
      if (unlocked) {
        phase.value = "idle";
        await run();
      }
    },
  );

  return {
    phase,
    error,
    run,
    markOnboardingComplete,
  };
}

/** Reseta estado + expoe refs (tests diretos + força re-boot). */
export { phase, error, reset };

// ─────────────────────────────────────────────────────────────────────────────
// Helpers internos
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Decide INITIALIZE state baseado em session restaurada.
 *
 * Lógica extraída de App.vue:85-104 (HLOS-46 + HLOS-45 v0.4.3):
 *   - messages.length > 0      → 'conversation' (estava em conversa)
 *   - lastTurnAt != null       → 'home' (já usou o app antes)
 *   - onboardingCompleted flag → 'home' (HLOS-45 v0.4.3 fix)
 *   - senão                    → manter INITIAL_STATE (que é 'onboarding')
 *
 * IMPORTANTE: emite evento INITIALIZE na state machine do App.vue.
 * Como o composable é Vue-only, expomos via window.dispatchEvent
 * (App.vue escuta) — alternativa: callback injectado via param.
 *
 * Para simplicidade, usamos um CustomEvent no window. App.vue
 * pode escutar via `window.addEventListener('hlos:initialize', ...)`.
 */
function decideInitialState(restored: boolean): void {
  let initialState: "onboarding" | "home" | "conversation" = "onboarding";

  if (restored) {
    const hasMessages = useConversationStore().messages.length > 0;
    const hadTurns = useCompanionStore().lastTurnAt !== null;
    if (hasMessages) {
      initialState = "conversation";
    } else if (hadTurns) {
      initialState = "home";
    } else if (useAuthStore().hasCompletedOnboarding()) {
      // HLOS-45 v0.4.3: skip onboarding se flag persistida.
      initialState = "home";
    }
  } else {
    // restoreSession falhou (vaultData sem session) — verifica flag.
    if (useAuthStore().hasCompletedOnboarding()) {
      initialState = "home";
    }
  }

  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent("hlos:initialize", { detail: { initialState } }),
    );
  }
}

/**
 * HLOS-54: YjsProvider lazy init via bootAfterUnlock.
 *
 * Apenas inicializa se `VITE_RELAY_BASE_URL` está definida (opt-in).
 * Em produção (Sprint 8+), relay endpoint será padrão.
 *
 * Idempotente: se provider já existe, no-op.
 */
async function tryInitializeYjsProvider(): Promise<void> {
  const relayBaseUrl = import.meta.env["VITE_RELAY_BASE_URL"] as
    | string
    | undefined;
  if (!relayBaseUrl) return;

  const { setYjsProvider, getYjsProvider } = await import("../sync/yjs-instance");
  if (getYjsProvider()) return; // idempotente

  const auth = useAuthStore();
  if (!auth.isUnlocked) {
    console.warn("[boot] YjsProvider skipped: vault not unlocked");
    return;
  }
  const vault = await getVaultAPI();
  const dek = vault.getDekBytes();
  // Deriva SHA-256(DEK) → roomIdBase (HLOS-54 fix: NUNCA vaza DEK como roomId).
  const vaultHash = await crypto.subtle.digest("SHA-256", dek as BufferSource);
  const roomIdBase = Array.from(new Uint8Array(vaultHash).slice(0, 8))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
  const encoder = new TextEncoder(); // reusado abaixo para enc.encode(plaintext)
  const cryptoKey = await crypto.subtle.importKey(
    "raw",
    dek as BufferSource,
    { name: "AES-GCM" },
    false,
    ["encrypt", "decrypt"],
  );
  const encDec = {
    async encrypt(plaintext: string): Promise<string> {
      const iv = crypto.getRandomValues(new Uint8Array(12));
      const ct = await crypto.subtle.encrypt(
        { name: "AES-GCM", iv: iv as BufferSource },
        cryptoKey,
        encoder.encode(plaintext) as BufferSource,
      );
      const merged = new Uint8Array(iv.length + ct.byteLength);
      merged.set(iv, 0);
      merged.set(new Uint8Array(ct), iv.length);
      return btoa(String.fromCharCode(...merged));
    },
    async decrypt(ciphertextB64: string): Promise<string> {
      const merged = Uint8Array.from(
        atob(ciphertextB64),
        (c) => c.charCodeAt(0),
      );
      const iv = merged.slice(0, 12);
      const ct = merged.slice(12);
      const pt = await crypto.subtle.decrypt(
        { name: "AES-GCM", iv: iv as BufferSource },
        cryptoKey,
        ct as BufferSource,
      );
      return new TextDecoder().decode(pt);
    },
  };

  const cfg = {
    baseUrl: relayBaseUrl,
    sessionId: "sess-" + roomIdBase,
    roomId: "room-" + roomIdBase,
    deviceId: "device-" + crypto.randomUUID().slice(0, 8),
    encrypt: encDec.encrypt,
    decrypt: encDec.decrypt,
    pullIntervalMs: 30_000,
  };
  const { YjsProvider } = await import("../sync/yjs-provider");
  const provider = new YjsProvider(cfg);
  await provider.initialize();
  setYjsProvider(provider);
  console.info(
    `[boot] YjsProvider initialized room=${cfg.roomId} device=${cfg.deviceId}`,
  );
}
